/** Shift cover for one date. Rules live in _shared/shiftExceptions.ts, labels and colours in lib/shiftCover.ts. */
import React, { useState } from "react"

import { format } from "date-fns"
import { AlertTriangle, CalendarX, ChevronDown, ChevronRight, UserX, X } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { BetaTag } from "@/components/ui/beta-tag"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useShiftExceptions } from "@/hooks/useCollections"
import { formatDayLong } from "@/lib/dateFormats"
import {
  COVER_BETA_NOTE,
  SEVERITY_ACTION,
  SEVERITY_LABEL,
  SEVERITY_ORDER,
  coverBadge,
  coverTextClass,
  describeCover,
  displayPerson,
  displayRouteCode,
  formatWindow,
  groupByRoute,
} from "@/lib/shiftCover"
import { toneClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"
import type { ShiftException, ShiftExceptionSeverity, ShiftExceptionType } from "@/types/collections"

interface ShiftExceptionsPanelProps {
  isOpen: boolean
  onClose: () => void
  selectedDate: Date
  depot?: string
  onHighlightRoute?: (routeInstanceId: string) => void
}

const SECTION_ICON: Record<ShiftExceptionSeverity, typeof UserX> = {
  unstaffed: UserX,
  drift: AlertTriangle,
  orphan: CalendarX,
}


const Times: React.FC<{ start: string | null; end: string | null; className?: string }> = ({
  start,
  end,
  className,
}) => (
  <span className={cn("shrink-0 tabular-nums text-[11px] text-palette-jungleGreen600", className)}>
    {formatWindow(start, end) ?? <span className="text-palette-jungleGreen300">&mdash;</span>}
  </span>
)

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="w-9 shrink-0 pt-px text-[9px] font-semibold uppercase tracking-wide text-palette-jungleGreen400">
    {children}
  </span>
)

/** Findings with no cell to colour: they are said in a line of text instead. */
const SAID_IN_WORDS: ShiftExceptionType[] = [
  "vehicle_mismatch",
  "vehicle_class_mismatch",
  "depot_mismatch",
  "duplicate_shift",
]

const RouteCard: React.FC<{
  rows: ShiftException[]
  onHighlightRoute?: (routeInstanceId: string) => void
}> = ({ rows, onHighlightRoute }) => {
  const first = rows[0]
  const clickable = !!first.routeInstanceId && !!onHighlightRoute
  const { code, run } = displayRouteCode(first.routeCode)
  const isOrphan = !first.routeInstanceId
  const { timeDiffers, driverDiffers } = describeCover(rows)

  // One line per distinct shift: a duplicate booking must show both.
  const shifts = [...new Map(rows.filter((r) => r.shiftId).map((r) => [r.shiftId, r])).values()]
  const said = [...new Map(rows.map((r) => [r.exceptionType, r])).values()].filter((r) =>
    SAID_IN_WORDS.includes(r.exceptionType),
  )
  // Show the note only where it says more than the code it was matched on.
  const rawNote = shifts.find((s) => s.shiftNotes)?.shiftNotes ?? null
  const squash = (v: string) => v.replace(/[^a-z0-9]/gi, "").toUpperCase()
  const note = rawNote && (isOrphan || squash(rawNote) !== squash(code)) ? rawNote : null
  const differs = cn(toneClasses("warning").text, "font-medium")

  const activate = () => {
    if (first.routeInstanceId) onHighlightRoute?.(first.routeInstanceId)
  }

  return (
    <div
      className={cn(
        "rounded-md border border-palette-jungleGreen200 bg-white px-3 py-2 space-y-1",
        clickable && "cursor-pointer hover:border-palette-jungleGreen400",
      )}
      onClick={clickable ? activate : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                activate()
              }
            }
          : undefined
      }
    >
      {!isOrphan && (
        <div className="flex items-start gap-1.5 min-w-0">
          <Label>Route</Label>
          <div className="min-w-0 flex-1 space-y-px">
            <div className="flex items-baseline justify-between gap-3 min-w-0">
              <span
                className="min-w-0 truncate text-xs font-semibold text-palette-jungleGreen900"
                title={first.routeCode ?? undefined}
              >
                {code}
                {run != null && run > 1 && (
                  <span className="ml-1 font-normal text-palette-jungleGreen500">run {run}</span>
                )}
              </span>
              <Times start={first.plannedStartTime} end={first.plannedEndTime} />
            </div>
            {first.driverName && (
              <div className={cn("truncate text-[11px] text-palette-jungleGreen700", driverDiffers && differs)}>
                {first.driverName}
              </div>
            )}
          </div>
        </div>
      )}

      {shifts.length === 0 ? (
        <div className="flex items-start gap-1.5">
          <Label>Shift</Label>
          <span className={cn("text-[11px]", coverTextClass("unstaffed"))}>No shift booked</span>
        </div>
      ) : (
        shifts.map((sh, i) => {
          const unfilled = sh.shiftStatus && sh.shiftStatus !== "Filled"
          const person = displayPerson(sh.shiftPersonName)
          return (
            <div key={sh.shiftId} className="flex items-start gap-1.5 min-w-0" title={sh.detail}>
              <Label>{i === 0 ? "Shift" : ""}</Label>
              <div className="flex min-w-0 flex-1 items-baseline justify-between gap-3">
                <span
                  className={cn(
                    "flex min-w-0 items-baseline gap-1.5 text-[11px] text-palette-jungleGreen800",
                    isOrphan && "text-xs font-semibold text-palette-jungleGreen900",
                    driverDiffers && differs,
                    unfilled && coverTextClass("unstaffed"),
                  )}
                >
                  <span className="min-w-0 truncate">{person ?? "Unassigned"}</span>
                  {unfilled && <span className="shrink-0 font-normal">{sh.shiftStatus}</span>}
                </span>
                <Times start={sh.shiftStart} end={sh.shiftEnd} className={cn(timeDiffers && differs)} />
              </div>
            </div>
          )
        })
      )}

      {(note || said.length > 0) && (
        <div className="space-y-px pl-[2.625rem]">
          {note && isOrphan ? (
            <div
              className="rounded bg-palette-jungleGreen000 px-1.5 py-1 text-[11px] font-medium text-palette-jungleGreen900 break-words"
              title={first.detail}
            >
              {note}
            </div>
          ) : (
            note && (
              <div className="truncate text-[10px] text-palette-jungleGreen500" title={note}>
                &ldquo;{note}&rdquo;
              </div>
            )
          )}
          {said.map((r) => (
            <div key={r.exceptionType} className="text-[11px] leading-snug text-palette-jungleGreen700">
              {r.detail}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const ShiftExceptionsPanel: React.FC<ShiftExceptionsPanelProps> = ({
  isOpen,
  onClose,
  selectedDate,
  depot,
  onHighlightRoute,
}) => {
  const dateStr = format(selectedDate, "yyyy-MM-dd")
  const { data, isLoading, error } = useShiftExceptions(
    { from: dateStr, to: dateStr, depot },
    isOpen,
  )
  const [collapsed, setCollapsed] = useState<Set<ShiftExceptionSeverity>>(new Set())

  const toggle = (severity: ShiftExceptionSeverity) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(severity)) next.delete(severity)
      else next.add(severity)
      return next
    })

  if (!isOpen) return null

  const coveredCount = data?.covered?.length ?? 0
  const headerBadge = coverBadge(data?.exceptions)

  return (
    <div className="w-96 flex-shrink-0 bg-white border-l border-palette-jungleGreen200 flex flex-col h-full shadow-lg">
      <div className="flex items-center justify-between px-4 py-3 border-b border-palette-jungleGreen200 bg-palette-jungleGreen000">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-palette-jungleGreen900">Shift cover</h3>
          <BetaTag title={COVER_BETA_NOTE} />
          {headerBadge && (
            <Badge
              className={cn("h-5 border px-1.5 text-xs", toneClasses(headerBadge.tone).badge)}
            >
              {headerBadge.count}
            </Badge>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
          <X size={14} />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-4">
          <div className="flex items-baseline justify-between gap-2 text-xs">
            <span className="text-palette-jungleGreen700">
              {formatDayLong(selectedDate)}
            </span>
            {data && !isLoading && (
              <span className="text-palette-jungleGreen500">
                {coveredCount} covered
              </span>
            )}
          </div>

          {isLoading && <p className="text-xs text-palette-jungleGreen500">Checking shifts…</p>}

          {error && (
            <p className="text-xs text-red-700">
              Couldn&apos;t read the shift comparison: {(error as Error).message}
            </p>
          )}

          {data?.truncated && (
            <p className="text-xs text-amber-700">
              Only part of the day could be read, so some routes are not accounted for here.
            </p>
          )}

          {data?.total === 0 && !data.truncated && (
            <p className="text-xs text-palette-jungleGreen600">
              Every route on this day has a matching shift.
            </p>
          )}

          {SEVERITY_ORDER.map((severity) => {
            const rows = data?.bySeverity?.[severity] ?? []
            if (rows.length === 0) return null
            const cards = groupByRoute(rows)
            const isCollapsed = collapsed.has(severity)
            const Icon = SECTION_ICON[severity]

            return (
              <div key={severity} className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggle(severity)}
                  aria-expanded={!isCollapsed}
                  className={cn(
                    "w-full flex items-center gap-2 text-xs transition-opacity hover:opacity-80",
                    coverTextClass(severity),
                  )}
                >
                  {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                  <Icon size={12} />
                  <span>
                    {SEVERITY_LABEL[severity]} ({cards.length})
                  </span>
                </button>

                {!isCollapsed && (
                  <>
                    <p className="text-[10px] uppercase tracking-wide text-palette-jungleGreen500 pl-6">
                      {SEVERITY_ACTION[severity]}
                    </p>
                    <div className="space-y-1.5">
                      {cards.map((card) => (
                        <RouteCard
                          key={card[0].routeInstanceId ?? `shift:${card[0].shiftId}`}
                          rows={card}
                          onHighlightRoute={onHighlightRoute}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )
          })}

          <p className="text-[10px] leading-snug text-palette-jungleGreen500">{COVER_BETA_NOTE}</p>
        </div>
      </ScrollArea>
    </div>
  )
}

export default ShiftExceptionsPanel
